import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Col, Row } from "reactstrap";
import MenuItem from "@material-ui/core/MenuItem";
import { Select } from "@material-ui/core";
import moment from "moment";
import localization from "moment/locale/id";

import { history } from "../../utils/History";
import {
  createBroadcastPreviewModel,
  getRequestErrorMessage,
} from "../../utils/winnerWorkflow";
import Table from "../../components/table";
import TableStatus from "../../components/table/status";
import SearchButton from "../../components/buttons/SearchButton";
import ResetFilterButton from "../../components/buttons/ResetFilterButton";
import ActiveFilterChips from "../../components/filters/ActiveFilterChips";
import FilteredEmptyState from "../../components/emptyState/FilteredEmptyState";
import HeaderMenu from "../../components/commons/menu/HeaderMenu";
import DetailButton from "../../components/table/detailButton";
import ConfirmationModal from "../../components/modal/ConfirmModal";
import { toastError, toastSuccess } from "../../components/commons/toast";
import useLatestRequest from "../../components/hooks/useLatestRequest";
import usePromotionFilterOptions from "../../components/hooks/usePromotionFilterOptions";
import { downloadExcelResponse, getExcelExportErrorMessage } from "../../utils/excelExport";
import Promo from "../../store/actions/promotion/Index";
import SidebarPromo from "./SidebarPromo";
import FilterModal from "./FilterWinnerModal";
import PreviewBroadcastModal from "./PreviewBroadcastModal";
import BroadcastConfirmationModal from "./BroadcastConfirmationModal";
import {
  createDetailLocation,
  enumParam,
  hasNonDefaultQuery,
  pageSizeParam,
  positivePage,
  stringParam,
  useListQueryState,
} from "../../utils/listQueryState";
import CopyableText from "../../components/commons/CopyableText";
import { optionLabel, quotedSearchLabel, summarizeFilterValues } from "../../utils/filterChips";

const STATUS_OPTIONS = [
  "Semua",
  "Dikonfirmasi",
  "Belum dikonfirmasi",
  "Diskualifikasi",
];

const WINNER_QUERY_SCHEMA = {
  page: positivePage,
  length: pageSizeParam,
  search: stringParam(),
  status: enumParam(["", "0", "1", "2"], ""),
  city_id: stringParam(),
  store: stringParam(),
  promo_return: stringParam(),
};

const WINNER_STATUS_LABELS = {
  "": "Semua",
  "0": "Belum dikonfirmasi",
  "1": "Dikonfirmasi",
  "2": "Diskualifikasi",
};

const WINNER_STATUS_VALUES = {
  Semua: "",
  "Belum dikonfirmasi": "0",
  Dikonfirmasi: "1",
  Diskualifikasi: "2",
};

const getWinnerPage = (dispatch, params) =>
  new Promise((resolve, reject) => {
    dispatch(Promo.getWinner(params, resolve, reject));
  });

const getAllWinners = async (dispatch, promotionId) => {
  const length = 250;
  let page = 1;
  let winners = [];
  let total = 0;

  do {
    const response = await getWinnerPage(dispatch, {
      page,
      length,
      id: promotionId,
      status: "",
      search_text: "",
      city_id: "",
      store: "",
      silent: true,
    });
    const pageData = response?.data ?? [];
    winners = winners.concat(pageData);
    total = response?.totalCount ?? winners.length;
    if (!pageData.length && winners.length < total) {
      throw new Error("Daftar penerima broadcast tidak lengkap.");
    }
    page += 1;
  } while (winners.length < total);

  return winners;
};

const PromoWinner = ({ match }) => {
  const id = match?.params?.id;
  const dispatch = useDispatch();
  const { beginRequest, isLatestRequest } = useLatestRequest();

  const [winners, setWinners] = useState([]);
  const [promo, setPromo] = useState();
  const { state: query, updateQuery, resetQuery, location } = useListQueryState(WINNER_QUERY_SCHEMA);
  const {
    page,
    length,
    search: searchText,
    status: statusFilter,
    city_id: filterCity,
    store: filterStore,
  } = query;
  const hasActiveFilters = hasNonDefaultQuery(
    query,
    WINNER_QUERY_SCHEMA,
    ["search", "status", "city_id", "store"]
  );
  const selectedStatus = WINNER_STATUS_LABELS[statusFilter];
  const [totalData, setTotalData] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [filterModal, setFilterModal] = useState(false);
  const [disqualificationModal, setDisqualificationModal] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState();
  const [regenerateModal, setRegenerateModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewModel, setPreviewModel] = useState();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const { cityOptions, storeOptions } = usePromotionFilterOptions({ cities: true, stores: true });

  const resetAllFilters = () => resetQuery(["page", "search", "status", "city_id", "store"]);
  const activeFilterChips = [
    searchText && {
      key: "search",
      label: quotedSearchLabel(searchText),
      onRemove: () => updateQuery({ page: 1, search: "" }),
    },
    statusFilter && {
      key: "status",
      label: `Status: ${WINNER_STATUS_LABELS[statusFilter] || statusFilter}`,
      onRemove: () => updateQuery({ page: 1, status: "" }),
    },
    filterCity && {
      key: "city_id",
      label: `Kota: ${summarizeFilterValues(filterCity, cityOptions)}`,
      onRemove: () => updateQuery({ page: 1, city_id: "" }),
    },
    filterStore && {
      key: "store",
      label: `Toko: ${optionLabel(filterStore, storeOptions)}`,
      onRemove: () => updateQuery({ page: 1, store: "" }),
    },
  ].filter(Boolean);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [broadcastError, setBroadcastError] = useState("");
  const previewLoadingRef = useRef(false);
  const broadcastingRef = useRef(false);
  const generatingRef = useRef(false);
  const exportRequestInFlight = useRef(false);

  const handleRefresh = () => {
    const requestId = beginRequest();
    setIsLoading(true);
    setLoadError(null);
    return getWinnerPage(dispatch, {
      page: page || 1,
      length,
      search_text: searchText,
      id,
      status: statusFilter,
      city_id: filterCity,
      store: filterStore,
    })
      .then((response) => {
        if (!isLatestRequest(requestId)) return;
        setWinners(response?.data ?? []);
        setTotalData(response?.totalCount ?? 0);
        const nextMaxPage = Math.max(1, Math.ceil((response?.totalCount ?? 0) / length));
        setMaxPage(nextMaxPage);
        if (page > nextMaxPage) updateQuery({ page: nextMaxPage });
      })
      .catch((error) => {
        if (!isLatestRequest(requestId)) return;
        setLoadError(getRequestErrorMessage(error, "Data pemenang gagal dimuat."));
      })
      .finally(() => {
        if (isLatestRequest(requestId)) setIsLoading(false);
      });
  };

  useEffect(() => {
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchText, statusFilter, filterCity, filterStore, page, length]);

  useEffect(() => {
    new Promise((resolve) => dispatch(Promo.detail({ id }, resolve))).then(
      (response) => setPromo(response?.data)
    );
  }, [dispatch, id]);

  const handleStatusChange = (event) => {
    updateQuery({
      page: 1,
      status: WINNER_STATUS_VALUES[event.target.value],
    });
  };

  const handleExportExcel = async () => {
    if (exportRequestInFlight.current || isLoading || !totalData) return;

    exportRequestInFlight.current = true;
    setIsExporting(true);

    try {
      const response = await dispatch(Promo.exportWinners({
        id,
        search_text: searchText,
        status: statusFilter,
        city_id: filterCity,
        store: filterStore,
      }));
      downloadExcelResponse(response, `pemenang-promo-${id}.xlsx`);
      toastSuccess("File Excel pemenang berhasil diunduh.");
    } catch (error) {
      const message = await getExcelExportErrorMessage(
        error,
        "Data pemenang gagal diekspor."
      );
      toastError(message);
    } finally {
      exportRequestInFlight.current = false;
      setIsExporting(false);
    }
  };

  const handleOpenBroadcastPreview = async () => {
    if (previewLoadingRef.current || broadcastingRef.current) return;
    previewLoadingRef.current = true;
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError("");
    setPreviewModel(undefined);
    try {
      const allWinners = await getAllWinners(dispatch, id);
      setPreviewModel(createBroadcastPreviewModel(allWinners, promo));
    } catch (error) {
      const message = getRequestErrorMessage(
        error,
        "Preview broadcast gagal disiapkan."
      );
      setPreviewError(message);
      toastError(message);
    } finally {
      previewLoadingRef.current = false;
      setPreviewLoading(false);
    }
  };

  const handleBroadcast = async () => {
    if (broadcastingRef.current || !previewModel || previewModel.blockers.length) return;
    broadcastingRef.current = true;
    setIsBroadcasting(true);
    setBroadcastError("");
    try {
      await dispatch(Promo.getBroadcast({ id }));
      setConfirmationOpen(false);
      setPreviewOpen(false);
      toastSuccess(
        `Broadcast ke ${previewModel.eligibleRecipients.length} pemenang berhasil.`
      );
      handleRefresh();
    } catch (error) {
      const message = getRequestErrorMessage(
        error,
        "Broadcast pemenang gagal. Silakan coba lagi."
      );
      setBroadcastError(message);
      toastError(message);
    } finally {
      broadcastingRef.current = false;
      setIsBroadcasting(false);
    }
  };

  const handleRegenerate = async () => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerateError("");
    setIsGenerating(true);
    try {
      const response = await dispatch(Promo.regenerateWinner({ id }));
      setRegenerateModal(false);
      history.push({
        pathname: `/participant/regenerate-winner/${id}`,
        search: location.search,
        state: {
          newData: response?.data ?? [],
          returnTo: `${location.pathname}${location.search}`,
          fromList: true,
        },
      });
    } catch (error) {
      const message = getRequestErrorMessage(
        error,
        "Generate ulang pemenang gagal."
      );
      setGenerateError(message);
      toastError(message);
    } finally {
      generatingRef.current = false;
      setIsGenerating(false);
    }
  };

  const handleDisqualification = () => {
    if (!selectedWinner) return;
    dispatch(
      Promo.disqualificationWinner(
        { voucher_id: selectedWinner.voucher_id },
        () => {
          setDisqualificationModal(false);
          handleRefresh();
        }
      )
    );
  };

  const columns = [
    { Header: "NO", accessor: "no" },
    {
      Header: "TANGGAL BROADCAST",
      accessor: "broadcasted_at",
      Cell: ({ row }) => row.original.broadcasted_at
        ? `${moment(row.original.broadcasted_at).locale("id", localization).format("L")}, ${moment(row.original.broadcasted_at).locale("id", localization).format("LT")}`
        : "-",
    },
    { Header: "NAMA PEMENANG", accessor: "full_name" },
    { Header: "NIK", accessor: "nik", Cell: ({ row }) => row.original.nik ?? "-" },
    {
      Header: "NO WHATSAPP",
      accessor: "phone_number",
      Cell: ({ value }) => (
        <CopyableText value={value} label="Nomor WhatsApp" />
      ),
    },
    { Header: "KOTA ASAL", accessor: "city_name" },
    {
      Header: "ID VOUCHER",
      accessor: "voucher_code",
      Cell: ({ value }) => (
        <CopyableText value={value} label="Kode voucher" />
      ),
    },
    { Header: "TOKO", accessor: "store", Cell: ({ row }) => row.original.store || "-" },
    {
      Header: "TANGGAL KONFIRMASI",
      accessor: "confirmation_at",
      Cell: ({ row }) => row.original.confirmation_at
        ? `${moment(row.original.confirmation_at).locale("id", localization).format("L")}, ${moment(row.original.confirmation_at).locale("id", localization).format("LT")}`
        : "-",
    },
    {
      Header: "STATUS",
      accessor: "status",
      Cell: ({ row }) => {
        const status = Number(row.original.status);
        return (
          <TableStatus
            status={status === 0 ? "Belum Dikonfirmasi" : status === 1 ? "Dikonfirmasi" : "Diskualifikasi"}
            color={status === 0 ? "notAvailable" : status === 1 ? "successLabel" : "disqualification"}
            outline
          />
        );
      },
    },
    {
      Header: "",
      accessor: "actions",
      Cell: ({ row }) => (
        <DetailButton>
          <MenuItem onClick={() => history.push(createDetailLocation(`/winner/detail/${row.original.promotion_id}/${row.original.customer_id}`, location))}>
            <span style={{ fontSize: 14 }}>Detail</span>
          </MenuItem>
          {Number(row.original.status) === 0 && (
            <MenuItem onClick={() => {
              setSelectedWinner(row.original);
              setDisqualificationModal(true);
            }}>
              <span style={{ fontSize: 14 }}>Diskualifikasi</span>
            </MenuItem>
          )}
        </DetailButton>
      ),
    },
  ];

  return (
    <div className="menu-container">
      <Row>
        <Col md={3} className="sidebar-promo-col">
          <SidebarPromo active="promo-winner" id={id} returnLocation={location} />
        </Col>
        <Col md={9}>
          <HeaderMenu
            title="Daftar Pemenang"
            toolbarClassName="winner-toolbar"
            secondaryContent={hasActiveFilters ? <ActiveFilterChips chips={activeFilterChips} /> : null}
          >
            <SearchButton
              placeholder="Cari pemenang / nomor wa"
              value={searchText}
              toggle={(value) => updateQuery({
                page: 1,
                search: value?.length > 0 ? value : "",
              })}
            />
            <div className="dropdown-winner rounded-button button-white">
              <Select value={selectedStatus} onChange={handleStatusChange} disableUnderline style={{ width: "100%" }}>
                {STATUS_OPTIONS.map((value) => <MenuItem value={value} key={value}>{value}</MenuItem>)}
              </Select>
            </div>
            <button type="button" className="button-broadcast" onClick={handleOpenBroadcastPreview} disabled={!promo || previewLoading || isBroadcasting}>
              Broadcast Pemenang
            </button>
            <button
              type="button"
              disabled={totalData === 0 || isGenerating}
              className={`button-generate-ulang ${totalData === 0 || isGenerating ? "disabled" : ""}`}
              onClick={() => setRegenerateModal(true)}
            >
              {isGenerating ? "Sedang generate..." : "Generate Ulang"}
            </button>
            <button type="button" className="button-filter-city" onClick={() => setFilterModal(true)}>Filter</button>
            <button
              type="button"
              className="button rounded-button primary button-default button-export-excel"
              onClick={handleExportExcel}
              disabled={isLoading || isExporting || !totalData}
            >
              {isExporting ? (
                <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Mengekspor...</>
              ) : (
                <><i className="fas fa-file-excel" aria-hidden="true" /> Export Excel</>
              )}
            </button>
            <ResetFilterButton
              visible={hasActiveFilters}
              onClick={resetAllFilters}
            />
          </HeaderMenu>
          <div className="content-container-side">
            <div className="overflow">
              <Table
                columns={columns}
                data={winners}
                loading={isLoading}
                error={loadError}
                onRetry={handleRefresh}
                emptyContent={hasActiveFilters ? <FilteredEmptyState onReset={resetAllFilters} /> : null}
                pagination={{
                  page,
                  totalData,
                  maxPage,
                  toggle: (nextPage) => updateQuery({ page: nextPage }),
                  length,
                  onPageSizeChange: (nextLength) => updateQuery({ page: 1, length: nextLength }),
                }}
              />
            </div>
          </div>
        </Col>
      </Row>

      <PreviewBroadcastModal
        show={previewOpen}
        toggle={() => !previewLoading && setPreviewOpen(false)}
        confirm={() => {
          setPreviewOpen(false);
          setBroadcastError("");
          setConfirmationOpen(true);
        }}
        model={previewModel}
        isLoading={previewLoading}
        error={previewError}
      />
      <BroadcastConfirmationModal
        show={confirmationOpen}
        count={previewModel?.eligibleRecipients.length ?? 0}
        cancel={() => !isBroadcasting && setConfirmationOpen(false)}
        confirm={handleBroadcast}
        isBroadcasting={isBroadcasting}
        error={broadcastError}
      />
      <ConfirmationModal
        show={disqualificationModal}
        toggle={() => setDisqualificationModal(false)}
        confirm={handleDisqualification}
        message="Apakah Anda yakin ingin mendiskualifikasi pemenang?"
        isConfirm="Ya, Diskualifikasi"
        icon="info"
        isDelete
      />
      <ConfirmationModal
        show={regenerateModal}
        toggle={() => !isGenerating && setRegenerateModal(false)}
        title="Generate Pemenang"
        confirm={handleRegenerate}
        isConfirm="Generate"
        icon="info"
        isLoading={isGenerating}
      >
        <div>
          <div>
            <p>Apakah Anda yakin ingin mengenerate ulang pemenang promo <b>{promo?.name}</b>?</p>
            {generateError && <div className="alert alert-danger" role="alert">{generateError}</div>}
          </div>
        </div>
      </ConfirmationModal>
      <FilterModal
        show={filterModal}
        toggle={() => setFilterModal(false)}
        action={(params) => {
          setFilterModal(false);
          updateQuery({
            page: 1,
            city_id: params.city,
            store: params.store,
          });
        }}
        id={id}
        filterCity={filterCity}
        filterStore={filterStore}
        cityOptions={cityOptions}
        storeOptions={storeOptions}
      />
    </div>
  );
};

export default PromoWinner;
